import { decipher, encipher } from "./EncryptionService";
import { FormikValues } from "formik";
import { FormikErrors } from "formik/dist/types";
import { gatherInitialValues } from "./FormService";
import {
    ClaimType,
    IncompleteIntakeInput,
    Member,
    MemberEmploymentInformation,
    MemberUnderwritingInformation,
    PartyType,
    WageFrequency
} from "../generated/graphql";
import { getKeyByValue } from "../component/enums/KeyFinder";
import { Party } from "../component/enums/Party";
import { BenefitType } from "../component/enums/BenefitType";
import { ApolloCache } from "@apollo/client";
import { DisabilityEmployerFormValues } from "../component/disability/DisabilityEmployerFormValues";
import { PayFrequency } from "../component/enums/PayFrequency";
import _ from "lodash";

export async function loadIncompleteIntake(encryptedFormData: string, incompleteIntakePassword: string,
    setValues: (values: any, shouldValidate?: boolean) => Promise<void | FormikErrors<any>>,
    apolloCache: ApolloCache<any>) {
    const newValues = await decipher(encryptedFormData, incompleteIntakePassword)
    const { values, cache } = JSON.parse(newValues);
    apolloCache.restore(cache)
    const initialValues = gatherInitialValues(values.claimId, values.benefit, values.party)
    const restoredValues = {
        ...initialValues,
        ...values
    };
    await setValues(restoredValues)
    return restoredValues;
}

export async function buildIncompleteIntake(formValues: FormikValues, apolloCache: ApolloCache<any>): Promise<IncompleteIntakeInput> {
    const { incompleteIntakePassword, documents, ...values } = formValues;

    const extractedCache = _.cloneDeep(apolloCache.extract())
    let cache;

    const employerValues = values as DisabilityEmployerFormValues;
    const privacyId = employerValues.privacyId;
    if (privacyId) {
        const findMemberQueries = Object.fromEntries(Object.entries(extractedCache.ROOT_QUERY).filter(([key]) => key.startsWith("findMember(") && key.includes(`"memberId":"${privacyId}"`)))
        cache = {
            ROOT_QUERY: {
                ...findMemberQueries,
                __typename: "Query"
            }
        } as any
        const memberCacheId = apolloCache.identify({ id: privacyId, __typename: 'Member' });
        const cachedMember = extractedCache[memberCacheId as string] as Member;
        if (memberCacheId && cachedMember) {
            const cachedEmploymentInformation = cachedMember.employmentInformation;
            cachedMember.employmentInformation = {
                ...cachedEmploymentInformation,
                dateOfHire: employerValues.dateOfHire ?? cachedEmploymentInformation?.dateOfHire,
                hoursWorked: parseFloat(employerValues.hoursWorkedPerWeek) ?? cachedEmploymentInformation?.hoursWorked
            } as MemberEmploymentInformation
            const cachedUnderwritingInformation = cachedMember.underwritingInformation;
            cachedMember.underwritingInformation = {
                ...cachedUnderwritingInformation,
                wage: parseFloat(employerValues.baseWage) ?? cachedUnderwritingInformation?.wage,
                wageFrequency: getKeyByValue(PayFrequency, employerValues.baseWageFrequency) as WageFrequency ?? cachedUnderwritingInformation?.wageFrequency,
                wageEffectiveDate: employerValues.baseWageEffectiveDate ?? cachedUnderwritingInformation?.wageEffectiveDate
            } as MemberUnderwritingInformation
            cache[memberCacheId] = cachedMember
        }
    }

    const encryptedFormData = await encipher(JSON.stringify({
        values,
        cache
    }), incompleteIntakePassword)
    return {
        claimId: values.claimId,
        emailAddress: values.incompleteIntakeEmailAddress,
        party: getKeyByValue(Party, values.party) as PartyType,
        benefit: getKeyByValue(BenefitType, values.benefit) as ClaimType,
        encryptedFormData
    }
}